/**
 * Basic Information Component
 * Displays and manages user's basic information
 */

import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, Calendar, Heart, Check } from 'lucide-react';

/**
 * Basic Information Component
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Basic information data
 * @param {boolean} props.isEditing - Whether the component is in edit mode
 * @param {Function} props.onChange - Function to handle data changes
 * @returns {JSX.Element} Basic information component
 */
const BasicInformation = ({ data, isEditing, onChange }) => {
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    phone: '',
    gender: '',
    interestedIn: [],
    birthdate: '',
    starSign: ''
  });
  
  // Gender options
  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'non-binary', label: 'Non-binary' },
    { value: 'prefer-not-to-say', label: 'Prefer not to say' }
  ];
  
  // Interest options
  const interestOptions = [
    { value: 'male', label: 'Men' },
    { value: 'female', label: 'Women' },
    { value: 'non-binary', label: 'Non-binary people' },
    { value: 'everyone', label: 'Everyone' }
  ];
  
  // Star sign options
  const starSignOptions = [
    'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 
    'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
  ];
  
  // Initialize form data
  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        username: data.username || '',
        email: data.email || '',
        phone: data.phone || '',
        gender: data.gender || '',
        interestedIn: data.interestedIn || [],
        birthdate: data.birthdate || '',
        starSign: data.starSign || ''
      });
    }
  }, [data]);
  
  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    onChange({ [name]: value });
  };
  
  // Handle interest change
  const handleInterestChange = (value) => {
    let updatedInterests;
    
    if (formData.interestedIn.includes(value)) {
      // Remove interest if already selected
      updatedInterests = formData.interestedIn.filter(interest => interest !== value);
    } else {
      // Add interest if not selected
      updatedInterests = [...formData.interestedIn, value];
    }
    
    setFormData((prev) => ({
      ...prev,
      interestedIn: updatedInterests
    }));
    onChange({ interestedIn: updatedInterests });
  };

  // Render edit mode
  if (isEditing) {
    return (
      <div className="space-y-6">
        {/* Name Input */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="name"
              id="name"
              value={formData.name}
              onChange={handleInputChange}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
              placeholder="Your full name"
            />
          </div>
        </div>
        
        {/* Username Input */}
        <div>
          <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
            Username
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500">@</span>
            </div>
            <input
              type="text"
              name="username"
              id="username"
              value={formData.username}
              onChange={handleInputChange}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
              placeholder="Your username"
              disabled={true} // Username cannot be changed
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">Username cannot be changed</p>
        </div>
        
        {/* Email Input */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleInputChange}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
              placeholder="Your email address"
              disabled={true} // Email cannot be changed
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
        </div>
        
        {/* Phone Input */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Phone className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="tel"
              name="phone"
              id="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
              placeholder="Your phone number"
            />
          </div>
        </div>
        
        {/* Gender Input */}
        <div>
          <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1">
            Gender
          </label>
          <select
            name="gender"
            id="gender"
            value={formData.gender}
            onChange={handleInputChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
          >
            <option value="">Select Gender</option>
            {genderOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        
        {/* Interested In */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            I'm Interested In
          </label>
          <div className="space-y-2">
            {interestOptions.map((option) => (
              <div key={option.value} className="flex items-center">
                <button
                  type="button"
                  onClick={() => handleInterestChange(option.value)}
                  className={`flex items-center justify-between w-full px-3 py-2 text-sm rounded-md border ${
                    formData.interestedIn.includes(option.value) 
                      ? 'border-green-500 bg-green-50 text-green-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>{option.label}</span>
                  {formData.interestedIn.includes(option.value) && (
                    <Check size={16} className="text-green-500" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
        
        {/* Birthdate Input */}
        <div>
          <label htmlFor="birthdate" className="block text-sm font-medium text-gray-700 mb-1">
            Birthdate
          </label>
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Calendar className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="date"
              name="birthdate"
              id="birthdate"
              value={formData.birthdate}
              onChange={handleInputChange}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
            />
          </div>
        </div>
        
        {/* Star Sign Input */}
        <div>
          <label htmlFor="starSign" className="block text-sm font-medium text-gray-700 mb-1">
            Star Sign
          </label>
          <select
            name="starSign"
            id="starSign"
            value={formData.starSign}
            onChange={handleInputChange}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
          >
            <option value="">Select Star Sign</option>
            {starSignOptions.map((sign) => (
              <option key={sign} value={sign}>
                {sign}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  // Render view mode
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-6">
        {/* Name */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Full Name</h3>
          <p className="mt-1 text-base text-gray-900">{formData.name || 'Not specified'}</p>
        </div>
        
        {/* Username */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Username</h3>
          <p className="mt-1 text-base text-gray-900">@{formData.username || 'Not specified'}</p>
        </div>
        
        {/* Email */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Email Address</h3>
          <p className="mt-1 text-base text-gray-900">{formData.email || 'Not specified'}</p>
        </div>
        
        {/* Phone */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Phone Number</h3>
          <p className="mt-1 text-base text-gray-900">{formData.phone || 'Not specified'}</p>
        </div>
        
        {/* Gender */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Gender</h3>
          <p className="mt-1 text-base text-gray-900">
            {genderOptions.find(option => option.value === formData.gender)?.label || 'Not specified'}
          </p>
        </div>
        
        {/* Interested In */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Interested In</h3>
          <div className="mt-1">
            {formData.interestedIn && formData.interestedIn.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {formData.interestedIn.map(interest => (
                  <span 
                    key={interest} 
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    {interestOptions.find(option => option.value === interest)?.label}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-base text-gray-900">Not specified</p>
            )}
          </div>
        </div>
        
        {/* Birthdate */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Birthdate</h3>
          <p className="mt-1 text-base text-gray-900">
            {formData.birthdate ? new Date(formData.birthdate).toLocaleDateString() : 'Not specified'}
          </p>
        </div>
        
        {/* Star Sign */}
        <div>
          <h3 className="text-sm font-medium text-gray-500">Star Sign</h3>
          <p className="mt-1 text-base text-gray-900">{formData.starSign || 'Not specified'}</p>
        </div>
      </div>
    </div>
  );
};

export default BasicInformation;